// Maps a semantic AgentAction to the existing Client input methods. The agent never issues
// raw keyboard/mouse — every action here is something a human player could also do.
//
// Movement actions set a *held intent* (a unit velocity vector) on the controller; the
// controller re-applies it each frame (see agent_controller.onFrame), because client-side
// reconciliation overwrites the local player's speed2 every frame. One-shot actions (attack,
// ability, pickup, etc.) call the corresponding Client sender immediately.
import { Protocol } from "game-common/protocol"

import { Client } from "../client"
import { AgentAction } from "./types"

// Item types that restore hunger when "use"d (mirrors packages/server/.../atoms/hunger.ts).
const FOOD_ITEMS = new Set(["drumstick", "mushroom-purple", "mushroom-yellow", "mushroom-red"])

/** Unit vectors for the compass directions the LLM may use. +y is south. */
const DIR_VECTORS: Record<string, { x: number; y: number }> = {
    N: { x: 0, y: -1 },
    S: { x: 0, y: 1 },
    E: { x: 1, y: 0 },
    W: { x: -1, y: 0 },
    NE: { x: 0.7071, y: -0.7071 },
    NW: { x: -0.7071, y: -0.7071 },
    SE: { x: 0.7071, y: 0.7071 },
    SW: { x: -0.7071, y: 0.7071 },
}

/** The controller surface the action dispatcher needs to set held movement. */
export interface MovementSink {
    setHeldMovement(v: { x: number; y: number } | null): void
}

/** Apply one action. Returns a short human-readable description for the activity log. */
export function applyAction(client: Client, sink: MovementSink, action: AgentAction): string {
    const player = client.clientGameLogic.playerEntity
    switch (action.type) {
        case "move_toward": {
            if (!player?.location) return "move_toward: no player"
            const dx = action.x - player.location.x
            const dy = action.y - player.location.y
            const mag = Math.sqrt(dx * dx + dy * dy)
            if (mag < 1) {
                sink.setHeldMovement(null)
                return "move_toward: already there"
            }
            sink.setHeldMovement({ x: dx / mag, y: dy / mag })
            return `move_toward (${Math.round(action.x)}, ${Math.round(action.y)})`
        }
        case "move": {
            const v = DIR_VECTORS[action.dir?.toUpperCase?.() || ""]
            if (!v) return `move: unknown dir ${action.dir}`
            sink.setHeldMovement(v)
            return `move ${action.dir}`
        }
        case "stop": {
            sink.setHeldMovement(null)
            return "stop"
        }
        case "attack": {
            if (!action.entityId) return "attack: no target"
            client.startAttack(action.entityId)
            return `attack ${action.entityId}`
        }
        case "chop": {
            // Strike a harvestable map tile (e.g. a tree) at the given world point. The server
            // only lands the strike if the player is adjacent, so aim at a nearby feature.
            client.entityTileStrike({ x: action.x, y: action.y })
            return `chop (${Math.round(action.x)}, ${Math.round(action.y)})`
        }
        case "eat": {
            // Find a food item in the live inventory and "use" it (restores hunger server-side).
            const food = (player?.inventory?.items || []).find(i => i.itemType && FOOD_ITEMS.has(i.itemType))
            if (!player || !food) return "eat: no food"
            client.inventoryAction(player.entityId, food.id, "use")
            return `eat ${food.itemType}`
        }
        case "use_ability": {
            client.activateAbility(action.key)
            return `use_ability ${action.key}`
        }
        case "pickup": {
            client.itemPickupAction()
            return "pickup"
        }
        case "drop": {
            client.dropAction()
            return "drop"
        }
        case "command_squad": {
            client.sendCommandWheelSelection(action.command, action.npcType, action.targetEntityId)
            return `command_squad ${action.command} (${action.npcType})`
        }
        case "say": {
            if (!player) return "say: no player"
            client.send({ entityId: player.entityId, message: action.message }, Protocol.type_chatMessage)
            return `say "${action.message}"`
        }
        case "wait":
            return "wait"
        default:
            return `unknown action ${(action as any).type}`
    }
}
